import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SharedUserMaterial,
  UserMaterial,
  isLink,
  isLti,
  isFile,
  isSharedUserMaterial,
  LtiUserMaterial,
} from '../../_api/UserMaterialApi';
import { getFileTypeDescription } from '../../../../utils/fileType';
import IdentityContext from '../../../../contexts/IdentityContext';
import { formatDate } from '../../../../utils/formatDate';
import formatTeacherName from '../../../../utils/formatTeacherName';
import ProgressionAndResultBar from '../../../../components/ProgressionAndResultBar';
import { RequiredFields } from '../../../../@types/utils';
import { getProvisionalScorePercentage } from '../../utils/scoreUtils';

const translationKey = 'MODULES.MATERIAL.COMPONENTS.MEDIALINK-MODALS.OPTIONS';

function isLtiWithScore(material: UserMaterial): material is RequiredFields<LtiUserMaterial, 'score'> {
  return isLti(material) && 'score' in material;
}

type UserMaterialInfoProps = {
  material: UserMaterial | SharedUserMaterial;
  nodeDescription: string;
};

function SharedInfo({ material }: Pick<UserMaterialInfoProps, 'material'>) {
  const { t } = useTranslation();

  if (isSharedUserMaterial(material)) {
    return (
      <>
        <dt>{t(`${translationKey}.SHARED_BY`)}</dt>
        <dd className="p-mb-2">{formatTeacherName(material.owner)}</dd>
      </>
    );
  } else {
    return (
      <>
        <dt>{t(`${translationKey}.SHARED.LABEL`)}</dt>
        <dd className="p-mb-2">
          {t(material.shared || material.assignment ? `${translationKey}.SHARED.YES` : `${translationKey}.SHARED.NO`)}
        </dd>
      </>
    );
  }
}

export default function UserMaterialInfo({ material, nodeDescription }: UserMaterialInfoProps) {
  const { t } = useTranslation();

  const { identity } = useContext(IdentityContext);

  const scorePercentage = isLtiWithScore(material) ? getProvisionalScorePercentage(material) : undefined;

  return (
    <dl className="definition-list">
      <dt>{t(`${translationKey}.UPDATED_AT_LABEL`)}</dt>
      <dd>{formatDate(material.lastUpdatedAt)}</dd>

      <dt>{t(`${translationKey}.BELONGS_TO`)}</dt>
      <dd className="p-mb-2">{nodeDescription}</dd>

      <SharedInfo material={material} />

      <dt>{t(`${translationKey}.CONTENTTYPE.LABEL`)}</dt>
      <dd>
        {identity.isTeacherAlike
          ? t(`${translationKey}.CONTENTTYPE.OWN-MATERIAL`)
          : t(`${translationKey}.CONTENTTYPE.SHARED-MATERIAL`)}
      </dd>

      <dt>{t(`${translationKey}.FILETYPE.LABEL`)}</dt>
      {isLink(material) && <dd>{t(`${translationKey}.FILETYPE.URL`)}</dd>}
      {isFile(material) && <dd>{t(`FILE_TYPE_DESCRIPTIONS.${getFileTypeDescription(material.file.mimeType).key}`)}</dd>}

      {isLti(material) && <dd className="p-mb-2">{t(`${translationKey}.FILETYPE.BOOKWIDGET`)}</dd>}
      {material.userMaxScore && (
        <>
          <dt>{t(`${translationKey}.SCORE_RESULT_LABEL`)}</dt>
          <dd className={scorePercentage ? 'p-mb-1' : undefined}>
            {isLtiWithScore(material) ? (
              <ProgressionAndResultBar
                achievable={material.score.achievable}
                achieved={material.score.achieved}
                maximum={material.score.maximum}
                isPending={material.needsGrading}
              />
            ) : (
              t(`${translationKey}.NO_SCORE_RESULT`)
            )}
          </dd>

          {scorePercentage && isLti(material) && !material.needsGrading && (
            <>
              <dt>{t(`${translationKey}.SCORE_PERCENTAGE_LABEL`)}</dt>
              <dd>{scorePercentage}</dd>
            </>
          )}
        </>
      )}
    </dl>
  );
}
